import React, { useEffect, useReducer, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import CustomTypeahead from '../../components/CustomTypeahead';
import accountService from '../../services/account.service';
import contactService from '../../services/contact.service';
import { getError, getUserInfo } from '../../utils';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import PropTypes from 'prop-types';
import { Button, FormField, Section, controlClass, textareaClass } from '../../components/EntityList';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

// Mirrors the backend's required-field contract (routes/contactRoutes.js
// POST '/' and PUT '/:id') so client and server never disagree about what's
// required. Trims so whitespace-only input doesn't pass as filled.
const FIELD_LABELS = {
	name: 'Nombre',
	email: 'Email',
};

export const getContactFieldErrors = ({ name, email }) => {
	const errors = {};
	if (!name || !String(name).trim()) errors.name = REQUIRED_FIELD_MESSAGE;
	if (!email || !String(email).trim()) errors.email = REQUIRED_FIELD_MESSAGE;
	return errors;
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_ACCOUNT_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_ACCOUNT_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_ACCOUNTS_SUCCESS':
		return { ...state, accounts: action.payload };
	case 'CREATE_REQUEST':
	case 'UPDATE_REQUEST':
		return { ...state, loadingCreate: true, error: '' };
	case 'CREATE_SUCCESS':
	case 'UPDATE_SUCCESS':
		return { ...state, loadingCreate: false, error: '' };
	case 'CREATE_FAIL':
	case 'UPDATE_FAIL':
		return { ...state, loadingCreate: false };
	case 'VALIDATION_FAIL':
		return { ...state, error: action.payload };
	default:
		return state;
	}
};

// eslint-disable-next-line no-unused-vars
const ContactEditor = ({ onClose, onSaved, initialData: _initialData = null, isEditing: _isEditing = false, cancelHandler, preloadAccountId = null, allowCreateRoundTrip = false }) => {
	const userInfo = getUserInfo();
	const createdBy = userInfo._id;
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Id injected via ?accountId= after a "crear nueva cuenta" round trip.
	// Only meaningful when the field isn't locked by preloadAccountId, and
	// only reachable when allowCreateRoundTrip is on (see the create link
	// below — modal call sites keep the link off entirely).
	const injectedAccountId = !preloadAccountId ? searchParams.get('accountId') : null;
	const consumedAccountId = useRef(null);
	// Stable snapshot for the edit-mode hydration guard below — syncInjectedParam
	// rewrites the live param as the user edits, so the guard must key off the
	// value present at mount, not the (now-mutable) injectedAccountId above.
	const injectedAccountIdAtMount = useRef(injectedAccountId);

	const [{ loading, loadingCreate, accounts, error }, dispatch] = useReducer(reducer, {
		loading: false,
		loadingCreate: false,
		accounts: [],
		error: '',
	});

	const [name, setName] = useState();
	const [email, setEmail] = useState();
	const [address, setAddress] = useState();
	const [phone, setPhone] = useState();
	const [account, setAccount] = useState(null);
	const [folder, setFolder] = useState();
	const [observations, setObservations] = useState();
	const [disabledAccount, setDisabledAccount] = useState(false);
	const [isInvalid, setIsInvalid] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	// Keep the URL mirroring the current selection so a manual pick or clear
	// doesn't leave a stale id from an earlier round trip to be re-injected.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	// Auto-select an account created via the "crear nueva cuenta" link, which
	// returns here with ?accountId=<id>. accounts is already refetched fresh
	// on mount (see fetchAccounts below), so find-in-list is enough.
	useEffect(() => {
		if (!injectedAccountId || consumedAccountId.current === injectedAccountId) return;
		const injectedAccount = accounts.find((acc) => acc._id === injectedAccountId);
		if (!injectedAccount) return;
		setIsInvalid(false);
		setAccount(injectedAccount);
		consumedAccountId.current = injectedAccountId;
	}, [accounts, injectedAccountId]);

	// Load initial data if editing
	useEffect(() => {
		const fetchData = async () => {
			if (_isEditing && _initialData?._id) {
				dispatch({ type: 'FETCH_REQUEST' });
				try {
					const [contactRes, accountsRes] = await Promise.all([
						contactService.getDetails(_initialData._id),
						accountService.list()
					]);
					const result = contactRes.data;
					setName(result.name);
					setEmail(result.email);
					setAddress(result.address);
					setPhone(result.phone);
					setFolder(result.folder);
					setObservations(result.observations);
					// Skip when an account was just injected via ?accountId= (see the
					// effect above) — otherwise whichever writer resolves last wins
					// nondeterministically.
					if (!injectedAccountIdAtMount.current) {
						setAccount(result.account || null);
					}
					dispatch({ type: 'FETCH_ACCOUNTS_SUCCESS', payload: accountsRes.data?.data || accountsRes.data || [] });
					dispatch({ type: 'FETCH_SUCCESS' });
				} catch (error) {
					dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
					toast.error('No se pudo obtener contacto');
				}
			} else if (_initialData) {
				// If initialData is provided but not editing (e.g., pre-filled data)
				setName(_initialData.name);
				setEmail(_initialData.email);
				setAddress(_initialData.address);
				setPhone(_initialData.phone);
				setFolder(_initialData.folder);
				setObservations(_initialData.observations);
				setAccount(_initialData.account || null);
			}
		};

		const fetchAccounts = async () => {
			try {
				dispatch({ type: preloadAccountId ? 'FETCH_ACCOUNT_REQUEST' : 'FETCH_REQUEST' });
				if (preloadAccountId) {
					const result = await accountService.getDetails(preloadAccountId);
					setAccount(result.data);
					setDisabledAccount(true);
					dispatch({ type: 'FETCH_ACCOUNT_SUCCESS' });
				} else {
					const result = await accountService.list();
					dispatch({ type: 'FETCH_ACCOUNTS_SUCCESS', payload: result.data?.data || result.data || [] });
				}
			} catch (error) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
				console.error('Error fetching accounts:', error);
			}
		};

		fetchAccounts();
		fetchData();
	}, [_isEditing, _initialData, preloadAccountId]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (loading || loadingCreate) {
			return;
		}

		const errors = getContactFieldErrors({ name, email });
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			dispatch({
				type: 'VALIDATION_FAIL',
				payload: `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`,
			});
			return;
		}
		setFieldErrors({});

		try {
			if (_isEditing) {
				dispatch({ type: 'UPDATE_REQUEST' });
				await contactService.edit(_initialData._id, {
					name,
					email,
					address,
					phone,
					account: account?._id || account,
					folder,
					observations,
					createdBy,
				});
				dispatch({ type: 'UPDATE_SUCCESS' });
				toast.success('Contacto actualizado correctamente');
			} else {
				dispatch({ type: 'CREATE_REQUEST' });
				await contactService.create({
					name,
					email,
					address,
					phone,
					account: account?._id || account,
					folder,
					observations,
					createdBy,
				});
				dispatch({ type: 'CREATE_SUCCESS' });
				toast.success('Contacto creado');
			}

			if (onSaved) {
				onSaved();
			}
			if (onClose) {
				onClose();
			}
		} catch (ex) {
			dispatch({ type: _isEditing ? 'UPDATE_FAIL' : 'CREATE_FAIL' });
			console.error(getError(ex));
			toast.error(_isEditing ? 'Error al actualizar contacto' : 'Error al crear contacto');
		}
	};

	const handleAccountChange = (selected) => {
		if (!selected || selected.length === 0) {
			setIsInvalid(false);
			setAccount(null);
			syncInjectedParam('accountId', null, consumedAccountId);
		} else {
			const selectedAccount = selected[0];
			const isValid = selectedAccount ? accounts.some(acc => acc._id === selectedAccount._id) : false;
			setIsInvalid(!isValid);
			setAccount(selectedAccount);
			syncInjectedParam('accountId', isValid ? selectedAccount._id : null, consumedAccountId);
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	return (
		<Form id='contact-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{_isEditing ? 'Actualizá los datos del contacto.' : 'Completá los datos para crear el contacto.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={cancelHandler || onClose} disabled={loadingCreate}>
						Cancelar
					</Button>
					<Button variant='primary' icon={_isEditing ? <FaRegSave /> : <MdAdd />} type='submit' disabled={loadingCreate}>
						{loadingCreate ? 'Guardando...' : _isEditing ? 'Guardar' : 'Agregar'}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title='Datos generales'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='contact-name' label='Nombre' required>
						<Form.Control
							id='contact-name'
							className={controlClass}
							onChange={(e) => {
								setName(e.target.value);
								clearFieldError('name');
							}}
							value={name || ''}
						/>
						<FieldError message={fieldErrors.name} />
					</FormField>
					<FormField id='contact-email' label='Email' required>
						<Form.Control
							id='contact-email'
							type='email'
							className={controlClass}
							onChange={(e) => {
								setEmail(e.target.value);
								clearFieldError('email');
							}}
							value={email || ''}
						/>
						<FieldError message={fieldErrors.email} />
					</FormField>
					<FormField id='contact-address' label='Dirección'>
						<Form.Control
							id='contact-address'
							className={controlClass}
							onChange={(e) => setAddress(e.target.value)}
							value={address || ''}
						/>
					</FormField>
					<FormField id='contact-phone' label='Teléfono'>
						<Form.Control
							id='contact-phone'
							className={controlClass}
							onChange={(e) => setPhone(e.target.value)}
							value={phone || ''}
						/>
					</FormField>
					{!preloadAccountId && (
						<FormField id='contact-account' label='Cuenta de contacto'>
							<CustomTypeahead
								id='contact-account'
								labelKey={(option) => `${option.name}`}
								onChange={handleAccountChange}
								options={accounts}
								placeholder='Buscar Cuenta... '
								selected={account ? [account] : []}
								disabled={disabledAccount}
								isInvalid={isInvalid}
								linkToCreate={allowCreateRoundTrip
									? `/AdminScreen/newAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`
									: ''}
								newOption='nueva cuenta'
								variant='entity'
							/>
							{isInvalid && <FieldError message='Cuenta invalida' />}
						</FormField>
					)}
					{preloadAccountId && (
						<FormField id='contact-account-readonly' label='Cuenta de contacto' required>
							<Form.Control
								id='contact-account-readonly'
								className={controlClass}
								readOnly
								value={account ? account.name : ''}
							/>
						</FormField>
					)}
					<FormField id='contact-folder' label='Carpeta'>
						<Form.Control
							id='contact-folder'
							className={controlClass}
							onChange={(e) => setFolder(e.target.value)}
							value={folder || ''}
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Observaciones' description='Notas internas o información adicional del contacto.'>
				<FormField id='contact-observations' label='Observaciones'>
					<Form.Control
						id='contact-observations'
						as='textarea'
						rows={8}
						className={textareaClass}
						onChange={(e) => setObservations(e.target.value)}
						value={observations || ''}
					/>
				</FormField>
			</Section>
		</Form>
	);
};

ContactEditor.propTypes = {
	onClose: PropTypes.func,
	onSaved: PropTypes.func,
	initialData: PropTypes.object,
	isEditing: PropTypes.bool,
	cancelHandler: PropTypes.func,
	preloadAccountId: PropTypes.string,
	allowCreateRoundTrip: PropTypes.bool,
};

export default ContactEditor;
